import type { ReactNode } from 'react';

import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { getReactQueryAxiosInstance } from '../api';
import { endpoints } from '../endpoints';

interface PushBenefitsToPMSRequest {
  unifiedAppointmentId: string;
  payerId?: number;
}

interface PushBenefitsToPMSResponse {
  success: boolean;
  message: ReactNode;
}

/**
 * React Query mutation hook for pushing benefits to PMS
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const usePushBenefitsToPMS = (
  options?: Parameters<
    typeof useMutation<
      PushBenefitsToPMSResponse,
      AxiosError,
      PushBenefitsToPMSRequest
    >
  >[0],
) => {
  const { t } = useLingui();
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;

  return useMutation({
    mutationFn: async (
      request: PushBenefitsToPMSRequest,
    ): Promise<PushBenefitsToPMSResponse> => {
      if (!currentOrganizationId || !currentPracticeId) {
        throw new Error('Missing organization or practice information');
      }

      const instance = getReactQueryAxiosInstance();

      const requestBody: { unifiedAppointmentId: string; payerId?: number } = {
        unifiedAppointmentId: request.unifiedAppointmentId,
      };
      if (request.payerId !== undefined) {
        requestBody.payerId = request.payerId;
      }

      // Call generate-print-view endpoint to get the practice document ID
      const generateResponse = await instance.request({
        method: 'POST',
        url: `/${endpoints.benefitsPdfGenerate}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: requestBody,
        responseType: 'blob',
      });

      // Extract practice document ID from response header
      const practiceDocumentId =
        generateResponse.headers['x-practice-document-id'];

      if (!practiceDocumentId) {
        throw new Error('Failed to get practice document ID from response');
      }

      // Call writeback endpoint
      await instance.request({
        method: 'POST',
        url: `/${endpoints.benefitsWriteback}`,
        headers: {
          Practice: currentPracticeId,
          Org: currentOrganizationId,
        },
        data: {
          practiceDocumentId,
        },
      });

      return {
        success: true,
        message: t`Successfully pushed to PMS`,
      };
    },
    ...options,
  });
};
